import { useRef, useState } from "react";
import type { DragEvent } from "react";
import MeritoButton from "../common/MeritoButtons/MeritoButtons";
import uploadIcon from "../../assets/images/bankStatements/upload.svg";
import styles from "./BankStatements.module.scss";

export type UploadDropzoneVariant = "default" | "firstTimeLanding" | "firstTimeReview";

const isPdfFileDrag = (event: DragEvent<HTMLDivElement>) => {
  const { dataTransfer } = event;
  if (!dataTransfer.types.includes("Files")) return false;
  if (!dataTransfer.items.length) return true;

  let hasFileItem = false;
  for (let index = 0; index < dataTransfer.items.length; index += 1) {
    const item = dataTransfer.items[index];
    if (item.kind !== "file") continue;
    hasFileItem = true;
    if (!item.type || item.type === "application/pdf") return true;
  }
  return !hasFileItem;
};

export const UploadDropzone = ({
  compact,
  dialogVariant,
  variant = "default",
  onBrowse,
  onFilesDropped,
}: {
  compact: boolean;
  dialogVariant: boolean;
  variant?: UploadDropzoneVariant;
  onBrowse: () => void;
  onFilesDropped: (files: File[]) => void;
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const dragDepthRef = useRef(0);
  const isFirstTimeLanding = variant === "firstTimeLanding";
  const isFirstTimeReview = variant === "firstTimeReview";
  const supportsDragActiveState =
    dialogVariant || isFirstTimeLanding || isFirstTimeReview;
  const wholeAreaOpensPicker =
    isFirstTimeLanding || (dialogVariant && !compact);

  const resetDragState = () => {
    const wasActive = dragDepthRef.current > 0;
    dragDepthRef.current = 0;
    if (wasActive) setIsDragActive(false);
  };

  return (
    <div
      className={`${styles.dropzone} ${
        compact ? styles.compactDropzone : ""
      } ${isFirstTimeLanding ? styles.firstTimeLandingDropzone : ""} ${
        isFirstTimeReview ? styles.firstTimeReviewDropzone : ""
      } ${
        supportsDragActiveState && isDragActive
          ? styles.dragActive
          : ""
      }`}
      onDragEnter={(event) => {
        if (!supportsDragActiveState || !isPdfFileDrag(event)) return;
        event.preventDefault();
        const wasInactive = dragDepthRef.current === 0;
        dragDepthRef.current += 1;
        if (wasInactive) setIsDragActive(true);
      }}
      onDragOver={(event) => {
        if (!isPdfFileDrag(event)) {
          event.dataTransfer.dropEffect = "none";
          return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={(event) => {
        if (!supportsDragActiveState || dragDepthRef.current === 0) return;
        event.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setIsDragActive(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        resetDragState();
        const files = Array.from(event.dataTransfer.files || []);
        if (files.length) onFilesDropped(files);
      }}
      onClick={wholeAreaOpensPicker ? onBrowse : undefined}
      onKeyDown={
        wholeAreaOpensPicker
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onBrowse();
              }
            }
          : undefined
      }
      role={wholeAreaOpensPicker ? "button" : undefined}
      tabIndex={wholeAreaOpensPicker ? 0 : undefined}
    >
      <span className={styles.uploadIcon}>
        <img src={uploadIcon} alt="" />
      </span>
      <span>
        <strong>
          {compact && !isFirstTimeLanding
            ? "Add more statements"
            : isFirstTimeLanding || dialogVariant
              ? "Drop files here or click to browse"
              : "Drop PDF statements here"}
        </strong>
        {compact || isFirstTimeReview ? (
          <small>Drop more files here or browse</small>
        ) : null}
      </span>
      {!wholeAreaOpensPicker ? (
        <MeritoButton
          type={dialogVariant || isFirstTimeReview ? "inverse" : "secondary-1"}
          size={dialogVariant ? "md" : "md2"}
          isRounded={dialogVariant}
          className={styles.selectFilesButton}
          icon={
            compact || isFirstTimeReview ? (
              <img className={styles.uploadButtonIcon} src={uploadIcon} alt="" />
            ) : undefined
          }
          btnText="Select files"
          onClick={onBrowse}
        />
      ) : null}
    </div>
  );
};
